import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Film, Trash2, Volume2, VolumeX, Loader2, Sparkles, Download, ExternalLink, ChevronDown, Wand2, Gauge } from "lucide-react";

const CHUNK = 5 * 1024 * 1024; // 5MB

// One optional 5-second motion clip that plays right after the title card, before the voiceover.
export const IntroClipStudio = ({ manualId, initial, slot = "intro", title, hint }) => {
  const [hasClip, setHasClip] = useState(!!(initial && (initial.storage_path || initial.file_id)));
  const [muted, setMuted] = useState(!!(initial && initial.muted));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [showHelp, setShowHelp] = useState(true);
  const fileRef = useRef(null);
  const base = `/manuals/${manualId}/clip/${slot}`;
  const heading = title || "Upload Your Video Intro Clip";
  const subhint = hint || "Plays right after your opening title card, before the voiceover begins — a great cinematic hook (made in your own tool, e.g. Kling).";
  const loadPreview = async () => {
    try {
      const { data } = await api.get(`${base}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setBlobUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch { /* preview optional */ }
  };

  useEffect(() => {
    if (hasClip) loadPreview();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file (MP4)."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Keep the clip to 5 seconds and under 5MB — compress it free at handbrake.fr first."); return; }
    setBusy(true); setProgress(0);
    try {
      const uploadId = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()).replace(/[^a-z0-9-]/gi, "");
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("chunk", blob, "chunk");
        await api.post(`${base}/upload-chunk`, fd);
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      await api.post(`${base}/finalize`, { upload_id: uploadId, muted });
      setHasClip(true);
      toast.success("Clip added ✓");
      await loadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
    } finally {
      setBusy(false); setProgress(0);
    }
  };

  const toggleMute = async () => {
    const next = !muted;
    setMuted(next);
    try { await api.patch(base, { muted: next }); }
    catch { setMuted(!next); toast.error("Couldn't update sound setting."); }
  };

  const removeClip = async () => {
    try {
      await api.delete(base);
      setHasClip(false);
      if (blobUrl) { URL.revokeObjectURL(blobUrl); setBlobUrl(null); }
      toast.success("Clip removed");
    } catch { toast.error("Couldn't remove the clip."); }
  };

  return (
    <div data-testid={`clip-studio-${slot}`}>
      <div className="flex items-center gap-2 font-extrabold text-lg text-white mb-1">
        <Film size={20} style={{ color: "#5d8a52" }} /> {heading}
      </div>
      <p className="text-[13px] text-[#a8a4ba] mb-3">
        <b className="text-white">5 seconds max · under 5MB.</b> {subhint}
      </p>

      {/* Friendly "no clue how" explainer — how to turn a step photo into a clip + compress it */}
      <div className="sms-tweed mb-4 rounded-2xl border-2 border-[#4f7746] bg-[#0f0f16] overflow-hidden" data-testid={`clip-help-${slot}`}>
        <button
          type="button"
          data-testid={`clip-help-toggle-${slot}`}
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left"
        >
          <Sparkles size={17} className="shrink-0 text-[#e0a13a]" />
          <span className="font-extrabold text-[14px] text-white flex-1">New to this? Here's how to make your clip</span>
          <ChevronDown size={18} className={`shrink-0 text-[#7fbf82] transition-transform ${showHelp ? "rotate-180" : ""}`} />
        </button>

        {showHelp && (
          <div className="px-4 pb-4 pt-1 space-y-3.5" data-testid={`clip-help-body-${slot}`}>
            <p className="text-[12.5px] text-[#c8c4d6] leading-relaxed">
              Your video already made you a set of photos — one for each step. You can bring any of them to life as a short moving clip and drop it in right here to help tell your story.
            </p>

            <div className="flex gap-2.5">
              <span className="shrink-0 w-6 h-6 rounded-full bg-[#1e3aaa] text-white text-[12px] font-extrabold flex items-center justify-center">1</span>
              <p className="text-[12.5px] text-[#c8c4d6] leading-relaxed">
                <span className="inline-flex items-center gap-1 font-bold text-white"><Download size={13} className="text-[#5b9bff]" /> Download one of your photos.</span> Back on the “Preview Before Publish” gallery, tap a photo and hit <b className="text-white">“Download this Image.”</b>
              </p>
            </div>

            <div className="flex gap-2.5">
              <span className="shrink-0 w-6 h-6 rounded-full bg-[#1e3aaa] text-white text-[12px] font-extrabold flex items-center justify-center">2</span>
              <div className="text-[12.5px] text-[#c8c4d6] leading-relaxed">
                <span className="inline-flex items-center gap-1 font-bold text-white"><Wand2 size={13} className="text-[#7fbf82]" /> Animate it in a video generator.</span> Upload that photo to your favorite AI video tool — e.g.{" "}
                <a href="https://www.kling.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-bold text-[#5b9bff] hover:underline">Kling.ai <ExternalLink size={11} /></a>{" "}
                — and type a simple prompt for a <b className="text-white">~5-second</b> clip that matches the shot:
                <ul className="mt-1.5 space-y-1">
                  <li className="flex items-start gap-1.5"><span className="text-[#e0a13a] mt-0.5">“</span><span className="italic">Car starts and drives away</span></li>
                  <li className="flex items-start gap-1.5"><span className="text-[#e0a13a] mt-0.5">“</span><span className="italic">A chef cuts a steak</span></li>
                  <li className="flex items-start gap-1.5"><span className="text-[#e0a13a] mt-0.5">“</span><span className="italic">A fitness instructor jumps rope</span></li>
                </ul>
                <span className="block mt-1.5 text-[#9a96ac]">Download the finished clip as an <b className="text-white">MP4</b>.</span>
              </div>
            </div>

            <div className="flex gap-2.5">
              <span className="shrink-0 w-6 h-6 rounded-full bg-[#1e3aaa] text-white text-[12px] font-extrabold flex items-center justify-center">3</span>
              <p className="text-[12.5px] text-[#c8c4d6] leading-relaxed">
                <span className="inline-flex items-center gap-1 font-bold text-white"><Gauge size={13} className="text-[#e57373]" /> Shrink it so it uploads fast.</span> Clips must be <b className="text-white">5 seconds and under 5MB</b>. Run it through the free{" "}
                <a href="https://www.handbrake.fr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-bold text-[#5b9bff] hover:underline">handbrake.fr <ExternalLink size={11} /></a>{" "}
                — it can turn a 10MB file into about 2.5MB with no visible quality loss, for quicker uploads and smoother playback.
              </p>
            </div>

            <div className="flex gap-2.5">
              <span className="shrink-0 w-6 h-6 rounded-full bg-[#4f7746] text-white text-[12px] font-extrabold flex items-center justify-center">4</span>
              <p className="text-[12.5px] text-[#c8c4d6] leading-relaxed">
                <span className="font-bold text-white">Upload it below</span> — that's it! We'll splice it seamlessly into your video.
              </p>
            </div>

            <p className="text-[11px] text-[#7c7791] italic pt-1 border-t border-[#2a2536] mt-1">
              Deeper step-by-step help videos are coming soon.
            </p>
          </div>
        )}
      </div>

      {!hasClip ? (
        <>
          <button
            type="button"
            data-testid={`clip-upload-btn-${slot}`}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#1e3aaa] hover:brightness-110 text-white font-extrabold py-3 disabled:opacity-60 transition-all"
          >
            {busy ? <><Loader2 size={17} className="spin" /> Uploading… {progress}%</> : <><Upload size={17} /> Upload a Clip (MP4)</>}
          </button>
          <p className="text-[11px] text-center text-[#9a96ac] mt-2">
            Optional · <b className="text-white/80">max 5 MB · 5 sec</b> · <a href="/faq" target="_blank" rel="noopener noreferrer" className="text-[#7fbf82] font-bold hover:underline" data-testid={`clip-faq-link-${slot}`}>see FAQ</a>
          </p>
        </>
      ) : (
        <div data-testid={`clip-loaded-${slot}`}>
          <div className="rounded-xl overflow-hidden bg-black border border-[#2a2536] mb-3" style={{ maxWidth: 360, margin: "0 auto" }}>
            {blobUrl ? (
              <video data-testid={`clip-preview-${slot}`} src={blobUrl} controls className="w-full block" style={{ maxHeight: 260 }} />
            ) : (
              <div className="flex items-center justify-center py-10 text-white/70"><Loader2 size={22} className="spin" /></div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`clip-mute-btn-${slot}`}
              onClick={toggleMute}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border-2 font-bold py-2.5 text-sm transition-colors"
              style={muted
                ? { borderColor: "#3a3550", background: "#171622", color: "#a8a4ba" }
                : { borderColor: "#4f7746", background: "#4f7746", color: "#fff" }}
            >
              {muted ? <><VolumeX size={15} /> Clip sound off</> : <><Volume2 size={15} /> Clip sound on</>}
            </button>
            <button
              type="button"
              data-testid={`clip-replace-btn-${slot}`}
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#171622] border-2 border-[#5d8a52] text-[#7fbf82] font-bold px-4 py-2.5 text-sm disabled:opacity-60 transition-colors"
            >
              {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} Replace
            </button>
            <button
              type="button"
              data-testid={`clip-remove-btn-${slot}`}
              onClick={removeClip}
              className="inline-flex items-center justify-center rounded-xl bg-[#171622] border-2 border-[#5a2b2b] text-[#e57373] px-3 py-2.5 text-sm transition-colors"
              aria-label="Remove clip"
            >
              <Trash2 size={15} />
            </button>
          </div>
          <p className="text-[11px] text-center text-[#9a96ac] mt-2">
            {muted ? "The clip plays silently; your music carries it." : "The clip's own sound plays, with your music mixed underneath."}
          </p>
        </div>
      )}
      <input ref={fileRef} type="file" accept="video/mp4,video/*" onChange={onPick} className="hidden" data-testid={`clip-file-input-${slot}`} />
    </div>
  );
};

export default IntroClipStudio;
